import React, { FC, useEffect, useState } from 'react';
import { Modal } from '@homeproved/shared/ui';
import { useTranslation } from 'react-i18next';
import { Features, HeaderText, ModalHeader, Title, Wrapper } from './Atoms';
import { Feature } from '../feature/Feature';
import { Box, useMediaQuery, useTheme } from '@material-ui/core';
import { useCookies } from 'react-cookie';
import { getURLWithoutSubdomain } from '@homeproved/shared/util';
import { PlanUid } from '../util/helpers';
import { CompanyData, Plan, PlanData } from '@homeproved/shared/data-access';

type PlanFeaturesProps = {
  plan: string;
  plans: Plan[];
  teamPlan: PlanData;
  modal?: boolean;
  company: CompanyData;
};

export const PlanFeatures: FC<PlanFeaturesProps> = ({
  plan,
  plans,
  teamPlan,
  modal = false,
  company,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.xs));
  const [showModal, setShowModal] = useState<boolean>(false);
  const [cookies, setCookie] = useCookies();
  const cookie = cookies['upgrade_modal'];

  const cookieDomain =
    typeof window !== 'undefined' ? getURLWithoutSubdomain(window.location.hostname) : undefined;

  const screenOne = useMediaQuery(theme.breakpoints.down(875));
  const screenTwo = useMediaQuery('(min-width:1040px) and (max-width:1200px)');

  const teamFeatures: string[] = t('app.pro.pages.upgradeModal.features', {
    returnObjects: true,
    defaultValue: [],
  });

  const planHasFeature = (feature: string) => {
    if (!feature === null) return false;
    return company.relations?.subscription?.data?.features[feature];
  };

  const handleClose = () => {
    setShowModal(false);
  };

  useEffect(() => {
    if (!modal) return;

    if (cookie === 'false' || cookie === undefined) {
      setShowModal(true);
      setCookie('upgrade_modal', true, {
        path: '/',
        domain: cookieDomain,
      });
    }
  }, [setShowModal, setCookie, modal, cookie, cookieDomain]);

  const ConditionalWrapper = ({ modal, children, showModal }) =>
    modal ? (
      <Modal
        open={showModal}
        onClose={handleClose}
        maxWidth={66}
        paddingTop={0}
        paddingBottom={0}
        background={'gradient'}
        featureModal={modal}
      >
        {children}
      </Modal>
    ) : (
      <Wrapper plan={plan} isMobile={isMobile}>
        {children}
      </Wrapper>
    );

  return (
    <ConditionalWrapper modal={modal} showModal={showModal}>
      {plan !== PlanUid.PRO && (
        <ModalHeader isMobile={isMobile} screenTwo={modal ? false : screenTwo}>
          {isMobile && (
            <HeaderText>
              <Title mobile>{t('app.pro.pages.upgradeModal.title')}</Title>
            </HeaderText>
          )}
        </ModalHeader>
      )}
      <Box width="100%" textAlign="center" color="#fff">
        <h2>De tools binnen jouw pakket</h2>
      </Box>
      <Features
        isMobile={isMobile}
        screenOne={modal ? false : screenOne}
        screenTwo={modal ? false : screenTwo}
        featureModal={modal}
      >
        {teamFeatures !== null &&
          teamFeatures.length > 0 &&
          teamFeatures.map((feature, index) => (
            <Feature
              key={index}
              isActive={planHasFeature(feature['key'])}
              title={feature['title']}
              text={feature['text']}
              isMobile={isMobile}
              icon={feature['icon']}
              iconColor={theme.palette.grey['800']}
              iconSize={2}
              gradient={true}
            />
          ))}
      </Features>
    </ConditionalWrapper>
  );
};
